import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { CreateTaskCommand, TaskFilters, TaskStatus, UpdateTaskCommand } from '../../types/domain'
import {
  addTaskUpdate,
  createTask,
  findTaskById,
  listTasks,
  listTaskUpdates,
  setTaskArchived,
  setTaskStatus,
  updateTask,
} from './task.repository'

export const taskKeys = {
  all: ['tasks'] as const,
  list: (filters: TaskFilters) => ['tasks', 'list', filters] as const,
  detail: (id: string) => ['tasks', 'detail', id] as const,
  history: (id: string) => ['tasks', 'history', id] as const,
}

export function useTasks(filters: TaskFilters) {
  return useQuery({ queryKey: taskKeys.list(filters), queryFn: () => listTasks(filters) })
}

export function useTask(id: string | undefined) {
  return useQuery({
    queryKey: taskKeys.detail(id ?? ''),
    queryFn: () => findTaskById(id ?? ''),
    enabled: Boolean(id),
  })
}

export function useTaskHistory(id: string) {
  return useQuery({ queryKey: taskKeys.history(id), queryFn: () => listTaskUpdates(id) })
}

function useInvalidateTasks() {
  const queryClient = useQueryClient()
  return (id?: string) => Promise.all([
    queryClient.invalidateQueries({ queryKey: taskKeys.all }),
    ...(id ? [queryClient.invalidateQueries({ queryKey: taskKeys.detail(id) })] : []),
  ])
}

export function useSaveTask() {
  const invalidate = useInvalidateTasks()
  return useMutation({
    mutationFn: (command: CreateTaskCommand | UpdateTaskCommand) =>
      findTaskById(command.id).then((existing) =>
        existing ? updateTask(command) : createTask(command),
      ),
    onSuccess: (task) => invalidate(task.id),
  })
}

export function useSetTaskStatus(id: string) {
  const invalidate = useInvalidateTasks()
  return useMutation({
    mutationFn: (status: TaskStatus) => setTaskStatus(id, status),
    onSuccess: () => invalidate(id),
  })
}

export function useSetTaskArchived(id: string) {
  const invalidate = useInvalidateTasks()
  return useMutation({
    mutationFn: (archived: boolean) => setTaskArchived(id, archived),
    onSuccess: () => invalidate(id),
  })
}

export function useAddTaskUpdate(taskId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (command: { id: string; note: string; files: File[] }) =>
      addTaskUpdate({ ...command, taskId }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: taskKeys.history(taskId) }),
  })
}
